import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAppState } from '../components/StateProvider';
import { useRecipeMemory } from '../components/RecipeProvider';
import { supabase } from '../lib/supabase';
import { importRecipeShareRemote, previewRecipeShareRemote, screenRecipeShareRemote, type RecipeSafetyReport, type SharedRecipePreview } from '../lib/recipeSharing';

export default function SharedRecipe() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { householdId, loading } = useAppState();
  const { refreshRecipes } = useRecipeMemory();
  const [recipe, setRecipe] = useState<SharedRecipePreview | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  const [importing, setImporting] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [fullAccess, setFullAccess] = useState(false);
  const [safety, setSafety] = useState<RecipeSafetyReport | null>(null);

  useEffect(() => {
    let alive = true;

    async function loadPreview() {
      if (!alive) return;
      setBusy(true);
      try {
        const result = await previewRecipeShareRemote(token);
        if (!alive) return;
        setRecipe(result.recipe);
        setExpiresAt(result.expiresAt);
        setNotice(result.notice);
        setFullAccess(Boolean(result.fullAccess));
        if (result.fullAccess) setSignedIn(true);
        setError('');
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'This private recipe link could not be opened.');
      } finally {
        if (alive) setBusy(false);
      }
    }

    async function init() {
      if (supabase) {
        const { data } = await supabase.auth.getSession();
        if (!alive) return;
        setSignedIn(Boolean(data.session));
      }
      await loadPreview();
    }

    void init();

    if (supabase) {
      const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
        if (!alive) return;
        const nextSignedIn = Boolean(session);
        setSignedIn(nextSignedIn);
        if (nextSignedIn) void loadPreview();
        else setFullAccess(false);
      });
      return () => { alive = false; listener.subscription.unsubscribe(); };
    }

    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!signedIn || !fullAccess || !householdId || !recipe) { setSafety(null); return; }
    let alive = true;
    void screenRecipeShareRemote(token, householdId).then((result) => { if (alive) setSafety(result.safety); }).catch((e) => { if (alive) console.warn('Shared recipe household screen failed', e); });
    return () => { alive = false; };
  }, [signedIn, fullAccess, householdId, recipe, token]);

  async function saveToWeekstead() {
    if (!householdId) return;
    setImporting(true); setError('');
    try {
      const result = await importRecipeShareRemote(token, householdId);
      setSafety(result.safety);
      await refreshRecipes();
      navigate('/recipes', { replace: true });
    } catch (e: any) {
      if (e?.payload?.safety) setSafety(e.payload.safety);
      setError(e instanceof Error ? e.message : 'This recipe could not be saved.');
    } finally { setImporting(false); }
  }

  const nextPath = `/share/${encodeURIComponent(token)}`;
  const ingredients = recipe?.ingredients ?? [];
  const instructions = recipe?.instructions ?? [];
  const ingredientCount = recipe?.ingredientCount ?? ingredients.length;

  if (busy || loading) return <div className="shared-recipe-page"><section className="shared-recipe-shell"><span className="eyebrow">Private Recipe Share</span><h1>Opening recipe…</h1></section></div>;
  if (error && !recipe) return <div className="shared-recipe-page"><section className="shared-recipe-shell shared-error"><span className="eyebrow">Private Recipe Share</span><h1>Link unavailable</h1><p>{error}</p><button className="outline" onClick={() => navigate('/')}>Open Weekstead</button></section></div>;
  if (!recipe) return null;

  return <div className="shared-recipe-page">
    <section className="shared-recipe-shell">
      <header className="shared-brand"><img src="/weekstead-icon.png" alt="" /><div><strong>Weekstead</strong><small>Private Recipe Share</small></div></header>
      <div className="shared-recipe-heading"><div><span className="eyebrow">Shared privately</span><h1>{recipe.name}</h1><p>{notice}</p></div><span className="time-pill"><Icon name="clock" /> {recipe.prepMinutes || '—'} min</span></div>
      <div className="shared-meta"><span>{recipe.servings} servings</span><span>{ingredientCount} ingredients</span><span>Expires {new Date(expiresAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</span></div>

      {!fullAccess ? <div className="shared-recipe-gate">
        <span className="eyebrow">Recipe details are waiting</span>
        <h2>Create your Weekstead account to view this recipe</h2>
        <p>Unlock the full ingredients and directions. Once your household is set up, Weekstead can also check the recipe against your saved food rules and let you keep it in your recipe memory.</p>
        <div className="shared-gate-actions">
          <button className="primary" onClick={() => navigate(`/login?mode=signup&next=${encodeURIComponent(nextPath)}`)}>Create account to view recipe</button>
          <button className="outline" onClick={() => navigate(`/login?next=${encodeURIComponent(nextPath)}`)}>I already have Weekstead</button>
        </div>
        <small>The person who shared this recipe is not identified to you.</small>
      </div> : <>
        {safety?.allergyConflicts.length ? <div className="share-safety blocked"><strong>Household allergy conflict detected;</strong><p>This recipe should not be imported.</p>{safety.allergyConflicts.map((item, index) => <small key={`${item.person}-${item.rule}-${index}`}>{item.person}: {item.rule} — matched {item.matches.join(', ')}</small>)}</div> : signedIn && householdId && safety ? <div className="share-safety clear"><strong>No recorded allergy conflict detected</strong><p>This is a screening aid, not a guarantee. Review the recipe yourself before cooking.</p></div> : null}

        {safety?.dietaryWarnings.length ? <div className="share-safety warning"><strong>Dietary-rule warning</strong>{safety.dietaryWarnings.map((item, index) => <small key={`${item.person}-${item.rule}-${index}`}>{item.person}: {item.rule} — check {item.matches.join(', ')}</small>)}</div> : null}

        <div className="shared-recipe-grid">
          <section><span className="eyebrow">Ingredients</span><div className="shared-ingredients">{ingredients.map((ingredient) => <div key={`${ingredient.name}-${ingredient.unit}`}><span>{ingredient.name}{ingredient.optional ? ' (optional)' : ''}</span><strong>{ingredient.quantity} {ingredient.unit}</strong></div>)}</div></section>
          <section><span className="eyebrow">Directions</span>{instructions.length ? <ol className="shared-steps">{instructions.map((step, index) => <li key={`${index}-${step.slice(0, 20)}`}><b>{index + 1}</b><span>{step}</span></li>)}</ol> : <p className="muted-copy">No saved cooking guide was included with this recipe.</p>}</section>
        </div>

        <div className="shared-review-note"><strong>Review before cooking.</strong> Private sharing hides the sender's identity from recipients. Weekstead checks recorded household rules after sign-in, but ingredient labels, substitutions and individual medical needs still require your own review.</div>

        <div className="shared-recipe-actions">
          {!householdId ? <button className="primary" onClick={() => navigate(`/onboarding?next=${encodeURIComponent(nextPath)}`)}>Finish household setup</button> : safety?.blocked ? <button className="primary" disabled>Import not recommended for this household</button> : <button className="primary" disabled={importing} onClick={() => void saveToWeekstead()}>{importing ? 'Saving…' : 'Save to my recipes'}</button>}
          <button className="ghost" onClick={() => navigate('/recipes')}>My recipes</button>
        </div>
      </>}
    </section>
  </div>;
}
